import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

const MediaLibrary = ({ filters, onFilterChange }) => {
  const { getAccountId } = useAuth();
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [mediaType, setMediaType] = useState('all'); // 'all', 'image', 'video'
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Separate input state
  const [sortBy, setSortBy] = useState('roas');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sortColumn, setSortColumn] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [categoricalOptions, setCategoricalOptions] = useState({});
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [textModalContent, setTextModalContent] = useState({ title: '', content: '' });
  const [showImageFeatures, setShowImageFeatures] = useState(true);
  const [showVideoFeatures, setShowVideoFeatures] = useState(true);
  const [showCustomLabels, setShowCustomLabels] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isHookAnalyzing, setIsHookAnalyzing] = useState(false);
  const [hookAnalysisResults, setHookAnalysisResults] = useState(null);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, assetUrl: '', mediaFormat: '', adName: '' });
  const [customLabelDefinitions, setCustomLabelDefinitions] = useState([]);
  const [customLabelValues, setCustomLabelValues] = useState({}); // Map of asset_id -> { label_key -> value }

  const adsPerPage = 20;

  useEffect(() => {
    fetchAds();
  }, [currentPage, mediaType, searchKeyword, sortBy, sortOrder, sortColumn, filters]);

  useEffect(() => {
    fetchCustomLabelDefinitions();
  }, []);

  useEffect(() => {
    if (ads.length > 0 && customLabelDefinitions.length > 0) {
      fetchCustomLabelValues();
    }
  }, [ads, customLabelDefinitions]);

  // Handle search button click
  const handleSearch = () => {
    setSearchKeyword(searchInput);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle Enter key in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // CSV Export functionality
  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const accountId = getAccountId();
      const params = new URLSearchParams({
        type: mediaType,
        sortBy: sortBy,
        sortOrder: sortOrder,
        export: 'true' // Special flag for export
      });
      
      // Use dashboard ad name filter if available, otherwise use local search keyword
      // Follow the same pattern as other tabs
      const adNameFilterObject = {
        keywords: filters?.adNameFilterKeywords || [],
        logic: filters?.adNameFilterLogic || 'AND',
        mode: filters?.adNameFilterMode || 'include'
      };
      
      let activeSearch = '';
      
      if (adNameFilterObject.keywords.length > 0) {
        // Use dashboard ad name filter - let backend handle the logic
        // Send all keywords to backend for proper logic handling
        activeSearch = adNameFilterObject.keywords.join(' ');
      } else if (searchKeyword.trim()) {
        activeSearch = searchKeyword.trim();
      }
      
      if (activeSearch) {
        params.append('search', activeSearch);
        params.append('searchLogic', adNameFilterObject.logic);
        params.append('searchMode', adNameFilterObject.mode);
      }
      
      // Use filters from props instead of internal state
      // Check for custom date range first
      if (filters?.dateRangeType === 'custom' && filters?.startDate && filters?.endDate) {
        params.append('startDate', filters.startDate);
        params.append('endDate', filters.endDate);
        console.log('📅 MediaLibrary using CUSTOM date range:', filters.startDate, 'to', filters.endDate);
      } else if (filters?.dateRange) {
        // Dashboard dateRange is just a number (selectedRange)
        params.append('days', filters.dateRange.toString() || '30');
        console.log('📅 MediaLibrary using PRESET date range:', filters.dateRange, 'days');
      } else {
        // Fallback to default 30 days if no filters provided
        params.append('days', '30');
        console.log('📅 MediaLibrary using DEFAULT date range: 30 days');
      }
      
      if (sortColumn) {
        params.append('sortColumn', sortColumn);
      }
      
      const response = await fetch(`/api/media-library/ads?${params}`, {
        headers: {
          'x-account-id': accountId
        }
      });
      
      const data = await response.json();
      
      if (data.success && data.ads.length > 0) {
        // Get all columns from the first ad
        const firstAd = data.ads[0];
        const columns = Object.keys(firstAd);
        
        // Create CSV header
        const headers = columns.map(col => {
          // Format column names for better readability
          if (col === 'raw_ad_id') return 'Ad ID';
          if (col === 'ad_name') return 'Ad Name';
          if (col === 'format') return 'Media Format';
          if (col === 'image_url') return 'Image URL';
          if (col === 'video_url') return 'Video URL';
          if (col === 'total_revenue') return 'Total Revenue';
          if (col === 'total_spend') return 'Total Spend';
          if (col === 'total_impressions') return 'Total Impressions';
          if (col === 'total_clicks') return 'Total Clicks';
          if (col === 'ctr') return 'CTR (%)';
          if (col === 'roas') return 'ROAS';
          if (col === 'status') return 'Status';
          
          // Format creative label columns
          if (col.startsWith('f_')) return col.replace('f_', 'Image ').replace(/_/g, ' ');
          if (col.startsWith('cf_')) return col.replace('cf_', 'Video ').replace(/_/g, ' ');
          if (col.startsWith('video_')) return col.replace('video_', 'Video ').replace(/_/g, ' ');
          
          return col.replace(/_/g, ' ');
        });
        
        // Create CSV rows
        const csvRows = [
          headers.join(','),
          ...data.ads.map(ad => 
            columns.map(col => {
              const value = ad[col];
              // Handle null/undefined values
              if (value === null || value === undefined) return '';
              // Escape commas and quotes in values
              const stringValue = String(value);
              if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
                return `"${stringValue.replace(/"/g, '""')}"`;
              }
              return stringValue;
            }).join(',')
          )
        ];
        
        // Create and download CSV
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        
        // Generate filename with date range
        let dateRangeStr = 'no_date_range';
        if (filters?.dateRange) {
          if (filters.dateRange.dateRangeType === 'custom') {
            dateRangeStr = `${filters.dateRange.startDate || 'start'}_to_${filters.dateRange.endDate || 'end'}`;
          } else {
            dateRangeStr = `last_${filters.dateRange.selectedRange}_days`;
          }
        }
        const filename = `media_library_${dateRangeStr}_${new Date().toISOString().split('T')[0]}.csv`;
        
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Show success message
        alert(`Successfully exported ${data.ads.length} ads to ${filename}`);
      } else {
        alert('No data to export. Please check your filters.');
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting CSV. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Render thumbnail component for feature analysis previews
  const renderThumbnail = (exampleAd, className = "w-16 h-16") => {
    if (!exampleAd) {
      return (
        <div className={`${className} bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs`}>
          No Example
        </div>
      );
    }

    const assetUrl = exampleAd.image_url || exampleAd.video_url;
    const mediaFormat = exampleAd.image_url ? 'Image' : 'Video';
    const adName = exampleAd.ad_name || 'Unknown Ad';

    if (!assetUrl) {
      return (
        <div className={`${className} bg-gray-200 rounded flex items-center justify-center text-gray-400 text-xs`}>
          No Asset
        </div>
      );
    }

    const openModal = () => {
      setPreviewModal({
        isOpen: true,
        assetUrl,
        adName,
        mediaFormat
      });
    };

    if (mediaFormat === 'Video') {
      return (
        <div className={`${className} relative bg-gray-200 rounded overflow-hidden cursor-pointer`} onClick={openModal}>
          <video
            src={assetUrl}
            className="w-full h-full object-cover"
            preload="metadata"
            onError={(e) => {
              if (e.target.nextSibling) {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-2xl">
            ▶️
          </div>
          <div 
            className="absolute inset-0 bg-gray-200 flex items-center justify-center text-gray-400 text-xs hidden"
          >
            Click to View
          </div>
        </div>
      );
    }

    return (
      <img
        src={assetUrl}
        alt={adName}
        className={`${className} object-cover rounded cursor-pointer hover:opacity-80 transition-opacity`}
        onError={(e) => {
          if (e.target.nextSibling) {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'flex';
          }
        }}
        onClick={openModal}
      />
    );
  };

  // Feature Analysis functionality
  const runFeatureAnalysis = async () => {
    setIsAnalyzing(true);
    try {
      const accountId = getAccountId();
      const params = new URLSearchParams({
        days: filters?.dateRange?.toString() || '90',
        minSpend: '50', // Reduced to get more data for analysis
        minImpressions: '100',
        analysisType: mediaType === 'all' ? 'video' : mediaType,
        correlationMetric: filters?.correlationMetric || 'revenue'
      });
      
      const response = await fetch(`/api/feature-analysis/feature-combinations?${params}`, {
        headers: {
          'x-account-id': accountId,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAnalysisResults(data);
      } else {
        console.error('Feature analysis failed:', data.error);
        alert('Feature analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Feature analysis error:', error);
      alert('Error running feature analysis. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Hook Analysis functionality
  const runHookAnalysis = async () => {
    setIsHookAnalyzing(true);
    try {
      const accountId = getAccountId();
      
      // Fetch ALL video ads (not just current page) for comprehensive analysis
      const params = new URLSearchParams({
        page: 1,
        limit: 10000, // Large limit to get all ads
        type: 'video', // Only video ads
        sortBy: 'thumbstop',
        sortOrder: 'desc'
      });

      // Use dashboard ad name filter if available
      const adNameFilterObject = {
        keywords: filters?.adNameFilterKeywords || [],
        logic: filters?.adNameFilterLogic || 'AND',
        mode: filters?.adNameFilterMode || 'include'
      };
      
      let activeSearch = '';
      if (adNameFilterObject.keywords.length > 0) {
        activeSearch = adNameFilterObject.keywords.join(' ');
      }
      
      if (activeSearch) {
        params.append('search', activeSearch);
        params.append('searchLogic', adNameFilterObject.logic);
        params.append('searchMode', adNameFilterObject.mode);
      }
      
      // Use filters from props
      if (filters?.dateRange) {
        params.append('days', filters.dateRange.toString() || '30');
      } else {
        params.append('days', '30');
      }

      const fetchResponse = await fetch(`/api/media-library/ads?${params}`, {
        headers: {
          'x-account-id': accountId
        }
      });
      
      const fetchData = await fetchResponse.json();
      
      if (!fetchData.success) {
        throw new Error('Failed to fetch video ads');
      }

      // Filter ads with spend threshold
      const filteredAds = fetchData.ads.filter(ad => 
        parseFloat(ad.total_spend || 0) >= 200 // Higher spend threshold for hook analysis
      );

      if (filteredAds.length === 0) {
        alert('No video ads found with spend >= $200. Please adjust your filters or spend threshold.');
        return;
      }

      const response = await fetch('/api/hook-analysis/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-account-id': accountId
        },
        body: JSON.stringify({
          ads: filteredAds,
          minSpend: 50,
          days: filters?.dateRange || 30
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setHookAnalysisResults(data);
      } else {
        console.error('Hook analysis failed:', data.error);
        alert('Hook analysis failed. Please try again.');
      }
    } catch (error) {
      console.error('Hook analysis error:', error);
      alert('Error running hook analysis. Please try again.');
    } finally {
      setIsHookAnalyzing(false);
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showMediaModal) {
        setShowMediaModal(false);
      }
    };

    if (showMediaModal) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showMediaModal]);

  // Handle video preview clicks
  useEffect(() => {
    const handleVideoPreviewClick = (e) => {
      const videoItem = e.target.closest('.video-preview-item');
      if (videoItem) {
        const videoUrl = videoItem.getAttribute('data-video-url');
        const adName = videoItem.getAttribute('data-ad-name');
        if (videoUrl && adName) {
          openVideoPreview(videoUrl, adName);
        }
      }
    };

    document.addEventListener('click', handleVideoPreviewClick);
    return () => document.removeEventListener('click', handleVideoPreviewClick);
  }, []);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const accountId = getAccountId();
      
      // Map correlation metrics to database field names
      const metricFieldMap = {
        'revenue': 'total_revenue',
        'roas': 'roas',
        'ctr': 'ctr',
        'spend': 'total_spend',
        'cpa': 'cpa',
        'thumbstop': 'thumbstop',
        'hold_rate': 'hold_rate'
      };
      
      const globalSortBy = metricFieldMap[filters?.correlationMetric] || 'roas';
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: adsPerPage,
        type: mediaType,
        sortBy: globalSortBy, // Use mapped database field name
        sortOrder: sortOrder
      });
      
      // Use dashboard ad name filter if available, otherwise use local search keyword
      // Follow the same pattern as other tabs
      const adNameFilterObject = {
        keywords: filters?.adNameFilterKeywords || [],
        logic: filters?.adNameFilterLogic || 'AND',
        mode: filters?.adNameFilterMode || 'include'
      };
      
      let activeSearch = '';
      
      if (adNameFilterObject.keywords.length > 0) {
        // Use dashboard ad name filter - let backend handle the logic
        // Send all keywords to backend for proper logic handling
        activeSearch = adNameFilterObject.keywords.join(' ');
      } else if (searchKeyword.trim()) {
        activeSearch = searchKeyword.trim();
      }
      
      if (activeSearch) {
        params.append('search', activeSearch);
        params.append('searchLogic', adNameFilterObject.logic);
        params.append('searchMode', adNameFilterObject.mode);
      }
      
      // Use filters from props instead of internal state
      // Check for custom date range first
      if (filters?.dateRangeType === 'custom' && filters?.startDate && filters?.endDate) {
        params.append('startDate', filters.startDate);
        params.append('endDate', filters.endDate);
        console.log('📅 MediaLibrary using CUSTOM date range:', filters.startDate, 'to', filters.endDate);
      } else if (filters?.dateRange) {
        // Dashboard dateRange is just a number (selectedRange)
        params.append('days', filters.dateRange.toString() || '30');
        console.log('📅 MediaLibrary using PRESET date range:', filters.dateRange, 'days');
      } else {
        // Fallback to default 30 days if no filters provided
        params.append('days', '30');
        console.log('📅 MediaLibrary using DEFAULT date range: 30 days');
      }
      
      if (sortColumn) {
        params.append('sortColumn', sortColumn);
      }
      
      const response = await fetch(`/api/media-library/ads?${params}`, {
        headers: {
          'x-account-id': accountId
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setAds(data.ads);
        setTotalPages(data.totalPages);
        setCategoricalOptions(data.categoricalOptions || {});
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomLabelDefinitions = async () => {
    try {
      const accountId = getAccountId();
      const response = await fetch('/api/custom-labels/definitions', {
        headers: {
          'x-account-id': accountId
        }
      });

      const data = await response.json();
      if (data.success) {
        setCustomLabelDefinitions(data.definitions || []);
      }
    } catch (error) {
      console.error('Error fetching custom label definitions:', error);
    }
  };

  const fetchCustomLabelValues = async () => {
    try {
      const accountId = getAccountId();
      const assetIds = ads.map(ad => ad.asset_id).filter(Boolean);
      
      if (assetIds.length === 0 || customLabelDefinitions.length === 0) return;

      // Fetch all custom labels for this account (we'll filter by asset_id in the frontend)
      const response = await fetch(`/api/custom-labels/assets?limit=10000`, {
        headers: {
          'x-account-id': accountId
        }
      });

      const data = await response.json();
      if (data.success) {
        // Create a map of asset_id -> label_key -> value
        const valuesMap = {};
        data.assets.forEach(asset => {
          if (asset.asset_id && asset.label_key && asset.label_value !== null) {
            if (!valuesMap[asset.asset_id]) {
              valuesMap[asset.asset_id] = {};
            }
            valuesMap[asset.asset_id][asset.label_key] = asset.label_value;
          }
        });
        console.log('Fetched custom label values:', valuesMap);
        setCustomLabelValues(valuesMap);
      }
    } catch (error) {
      console.error('Error fetching custom label values:', error);
    }
  };

  const handleCellEdit = (adId, field, currentValue, labelDefinition = null) => {
    setEditingCell(`${adId}-${field}`);
    setEditingValue(currentValue || '');
  };

  const handleCellSave = async (adId, field, newValue) => {
    try {
      const accountId = getAccountId();
      
      // Check if this is a custom label
      const isCustomLabel = field.startsWith('custom_');
      let updateResponse;
      
      if (isCustomLabel) {
        // Extract label key from field name (custom_label_key)
        const labelKey = field.replace('custom_', '');
        // Find the asset_id for this ad
        const ad = ads.find(a => a.raw_ad_id === adId);
        if (!ad || !ad.asset_id) {
          console.error('Asset ID not found for ad:', adId);
          return;
        }
        
        // Update custom label
        updateResponse = await fetch(`/api/custom-labels/assets/${ad.asset_id}/${labelKey}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'x-account-id': accountId
          },
          body: JSON.stringify({
            labelValue: newValue
          })
        });
      } else {
        // Update standard label
        updateResponse = await fetch('/api/media-library/update-label', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-account-id': accountId
          },
          body: JSON.stringify({
            adId,
            field,
            value: newValue
          })
        });
      }

      const data = await updateResponse.json();
      if (data.success) {
        if (isCustomLabel) {
          // Update custom label values state
          const labelKey = field.replace('custom_', '');
          const ad = ads.find(a => a.raw_ad_id === adId);
          if (ad && ad.asset_id) {
            setCustomLabelValues(prev => ({
              ...prev,
              [ad.asset_id]: {
                ...prev[ad.asset_id],
                [labelKey]: newValue
              }
            }));
          }
        } else {
          // Update local state for standard labels
          setAds(prevAds => 
            prevAds.map(ad => 
              ad.raw_ad_id === adId 
                ? { ...ad, [field]: newValue }
                : ad
            )
          );
        }
      }
    } catch (error) {
      console.error('Error updating label:', error);
    } finally {
      setEditingCell(null);
      setEditingValue('');
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const handleColumnSort = (columnKey) => {
    if (sortColumn === columnKey) {
      // Toggle sort order if same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(columnKey);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortOrder === 'asc' ? <span className="text-blue-600">↑</span> : <span className="text-blue-600">↓</span>;
  };

  const getFeatureBackgroundColor = (featureType) => {
    switch (featureType) {
      case 'image':
        return 'bg-blue-50'; // Light blue for image features
      case 'video':
        return 'bg-green-50'; // Light green for video features
      case 'common':
        return 'bg-purple-50'; // Light purple for common features
      case 'custom':
        return 'bg-yellow-50'; // Light yellow for custom labels
      default:
        return 'bg-white'; // Default white for performance columns
    }
  };

  // Long text content fields that should show in popup
  const longTextFields = [
    'cf_audio_text_content',
    'cf_text_overlay_content', 
    'cf_visual_description'
  ];

  const handleTextContentClick = (field, value, adName) => {
    if (longTextFields.includes(field) && value && value.trim()) {
      setTextModalContent({
        title: `${field.replace(/^[fc]_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} - ${adName}`,
        content: value
      });
      setShowTextModal(true);
    }
  };

  const renderMediaPreview = (ad) => {
    const handleMediaClick = () => {
      setSelectedMedia({
        type: ad.format,
        url: ad.format === 'Image' ? ad.image_url : ad.video_url,
        name: ad.ad_name,
        adId: ad.raw_ad_id
      });
      setShowMediaModal(true);
    };

    if (ad.format === 'Image' && ad.image_url) {
      return (
        <div 
          className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleMediaClick}
        >
          <img 
            src={ad.image_url} 
            alt={ad.ad_name}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          />
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs" style={{display: 'none'}}>
            📷
          </div>
        </div>
      );
    } else if (ad.format === 'Video' && ad.video_url) {
      return (
        <div 
          className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex items-center justify-center relative cursor-pointer hover:opacity-80 transition-opacity"
          onClick={handleMediaClick}
        >
          <video 
            className="w-full h-full object-cover"
            muted
            onError={(e) => {
              e.target.style.display = 'none';
              if (e.target.nextSibling) {
                e.target.nextSibling.style.display = 'flex';
              }
            }}
          >
            <source src={ad.video_url} type="video/mp4" />
          </video>
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
            <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center">
              <div className="w-0 h-0 border-l-2 border-r-0 border-t-1 border-b-1 border-transparent border-l-gray-800 ml-0.5"></div>
            </div>
          </div>
          <div className="w-full h-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs" style={{display: 'none'}}>
            🎥
          </div>
        </div>
      );
    }
    
    return (
      <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-500 text-xs">
        {ad.format === 'Image' ? '📷' : ad.format === 'Video' ? '🎥' : '❓'}
      </div>
    );
  };

  const getFieldType = (field) => {
    // Binary fields
    const binaryFields = [
      'f_face_present', 'f_direct_gaze', 'f_emotional_expression', 'f_professional_style',
      'f_ugc_style', 'f_product_shown', 'f_bright_colors', 'f_neutral_colors',
      'f_celebrity_shown', 'f_multiple_subjects', 'f_before_after_visual',
      'f_split_screen_horiz', 'f_split_screen_vert', 'f_motion_cue', 'f_graphic_effects',
      'f_star_rating', 'f_poor_quality', 'f_blurry', 'f_text_overlay_presence',
      'cf_creator_face_close_up', 'cf_person_speaking_to_camera', 'cf_emotional_reaction',
      'cf_professional_videography', 'cf_ugc_amateur_videography', 'cf_product_demonstrated',
      'cf_bright_colors', 'cf_neutral_colors', 'cf_celebrity_shown', 'cf_multiple_creator_shown',
      'cf_before_after_benefits_demonstrated', 'cf_media_split_screen_horizontal',
      'cf_media_split_screen_vertical', 'cf_dynamic_motion', 'cf_animation',
      'cf_transition_effects', 'cf_poor_lighting', 'cf_blurry_out_of_focus',
      'cf_voiceover', 'cf_music_playing', 'cf_silence', 'cf_asmr_sound_effects',
      'cf_captions_and_voiceover_match', 'cf_brand_mentioned_in_captions',
      'cf_problem_statement', 'cf_question', 'cf_curiosity_gap', 'cf_emojis_used',
      'cf_online_review_screenshot', 'cf_tiktok_comment'
    ];

    // Categorical fields
    const categoricalFields = [
      'f_ad_type', 'f_product_category', 'f_value_proposition', 'f_talent_age_range',
      'f_talent_diversity', 'f_talent_gender', 'f_talent_quantity', 'f_talent_relationship',
      'f_talent_type', 'f_talent_actions', 'f_call_to_action_type', 'f_price_display',
      'f_promotion_type', 'f_social_proof_type', 'f_urgency_element', 'f_text_alignment',
      'f_text_background', 'f_text_case', 'f_text_color',
      'f_text_font', 'f_text_position', 'f_text_size', 'f_text_weight',
      'f_text_overlays', 'f_brand_elements', 'f_camera_work', 'f_content_category',
      'f_dominant_color', 'f_graphics_or_effects', 'f_key_moments', 'f_lighting',
      'f_logo_position', 'f_person_position', 'f_product_position',
      'video_ad_type', 'video_product_category', 'video_value_proposition',
      'cf_audio_quality', 'cf_audio_volume', 'cf_background_noise', 'cf_speaking_pace',
      'cf_voice_accent', 'cf_voice_age', 'cf_voice_gender', 'cf_voice_presence',
      'cf_voice_tone', 'cf_talent_age_range', 'cf_talent_diversity', 'cf_talent_gender',
      'cf_talent_quantity', 'cf_talent_relationship', 'cf_talent_type', 'cf_talent_actions',
      'cf_music_genre', 'cf_music_mood', 'cf_music_presence', 'cf_music_tempo',
      'cf_music_volume', 'cf_sound_effects', 'cf_call_to_action_type', 'cf_price_display',
      'cf_promotion_type', 'cf_social_proof_type', 'cf_urgency_element', 'cf_text_alignment',
      'cf_text_animation', 'cf_text_background', 'cf_text_case', 'cf_text_color',
      'cf_text_font', 'cf_text_overlay_presence', 'cf_text_position', 'cf_text_size',
      'cf_text_weight', 'cf_text_overlays', 'cf_brand_elements', 'cf_camera_work',
      'cf_content_category', 'cf_dominant_color', 'cf_graphics_or_effects', 'cf_key_moments',
      'cf_lighting', 'cf_logo_position', 'cf_person_position', 'cf_product_position',
      'cf_product_presentation', 'cf_setting', 'cf_transitions'
    ];

    if (binaryFields.includes(field)) return 'binary';
    if (categoricalFields.includes(field)) return 'categorical';
    return 'text';
  };

  const renderEditableCell = (ad, field, value, labelDefinition = null) => {
    const cellKey = `${ad.raw_ad_id}-${field}`;
    const isCustomLabel = labelDefinition !== null;
    const fieldType = isCustomLabel ? labelDefinition.label_type : getFieldType(field);
    const isEditing = editingCell === cellKey;

    // For custom labels, always show them (they apply to both image and video)
    if (!isCustomLabel) {
      // Check if field is relevant to ad format
      const isImageField = field.startsWith('f_') && !field.startsWith('cf_');
      const isVideoField = field.startsWith('cf_') || field.startsWith('video_');
      
      const isRelevantField = (ad.format === 'Image' && isImageField) || 
                             (ad.format === 'Video' && isVideoField);

      if (!isRelevantField) {
        // Show N/A for irrelevant fields and make them non-editable
        return (
          <div className="p-2 text-gray-400">
            N/A
          </div>
        );
      }
    }

    if (!isEditing) {
      // Special handling for long text content fields
      if (longTextFields.includes(field)) {
        return (
          <div 
            className="cursor-pointer hover:bg-gray-50 p-2 rounded"
            onClick={() => handleTextContentClick(field, value, ad.ad_name)}
          >
            <div className="truncate max-w-xs" title={value || '0'}>
              {value || '0'}
            </div>
          </div>
        );
      }
      
      return (
        <div 
          className="cursor-pointer hover:bg-gray-50 p-2 rounded"
          onClick={() => handleCellEdit(ad.raw_ad_id, field, value, labelDefinition)}
        >
          {value !== null && value !== undefined && value !== '' ? String(value) : '-'}
        </div>
      );
    }

    if (fieldType === 'binary') {
      return (
        <div className="flex gap-2">
          <select
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">-</option>
            <option value="1">Yes</option>
            <option value="0">No</option>
          </select>
          <button
            onClick={() => handleCellSave(ad.raw_ad_id, field, editingValue)}
            className="bg-green-500 text-white px-2 py-1 rounded text-xs"
          >
            ✓
          </button>
          <button
            onClick={handleCellCancel}
            className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
          >
            ✕
          </button>
        </div>
      );
    }

    if (fieldType === 'categorical') {
      const options = isCustomLabel 
        ? (labelDefinition.categorical_values || [])
        : (categoricalOptions[field] || []);
      return (
        <div className="flex gap-2">
          <select
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          >
            <option value="">-</option>
            {options.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
          <button
            onClick={() => handleCellSave(ad.raw_ad_id, field, editingValue)}
            className="bg-green-500 text-white px-2 py-1 rounded text-xs"
          >
            ✓
          </button>
          <button
            onClick={handleCellCancel}
            className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
          >
            ✕
          </button>
        </div>
      );
    }

    // Text field
    return (
      <div className="flex gap-2">
        <input
          type="text"
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          className="border rounded px-2 py-1 text-sm flex-1"
        />
        <button
          onClick={() => handleCellSave(ad.raw_ad_id, field, editingValue)}
          className="bg-green-500 text-white px-2 py-1 rounded text-xs"
        >
          ✓
        </button>
        <button
          onClick={handleCellCancel}
          className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
        >
          ✕
        </button>
      </div>
    );
  };

  const getVisibleColumns = () => {
    if (ads.length === 0) return [];
    
    const firstAd = ads[0];
    const columns = [
      { key: 'media_preview', label: 'Media', type: 'preview', sortable: false },
      { key: 'ad_name', label: 'Ad Name', type: 'text', sortable: true },
      { key: 'format', label: 'Format', type: 'text', sortable: true },
      { key: 'total_impressions', label: 'Impressions', type: 'number', sortable: true },
      { key: 'total_clicks', label: 'Clicks', type: 'number', sortable: true },
      { key: 'ctr', label: 'CTR %', type: 'number', sortable: true },
      { key: 'total_spend', label: 'Spend', type: 'currency', sortable: true },
      { key: 'total_revenue', label: 'Revenue', type: 'currency', sortable: true },
      { key: 'roas', label: 'ROAS', type: 'number', sortable: true },
      { key: 'thumbstop', label: 'Thumbstop %', type: 'number', sortable: true },
      { key: 'hold_rate', label: 'Hold Rate %', type: 'number', sortable: true }
    ];

    // Add all available label columns (exclude URL columns)
    Object.keys(firstAd).forEach(key => {
      if ((key.startsWith('f_') || key.startsWith('cf_') || key.startsWith('video_')) && 
          key !== 'video_url' && key !== 'image_url') {
        
        // Determine feature type for background color
        let featureType = 'common';
        if (key.startsWith('f_') && !key.startsWith('cf_')) {
          featureType = 'image';
        } else if (key.startsWith('cf_') || key.startsWith('video_')) {
          featureType = 'video';
        }
        
        // Filter columns based on fold/unfold state
        const shouldShowColumn = 
          (featureType === 'image' && showImageFeatures) ||
          (featureType === 'video' && showVideoFeatures) ||
          (featureType === 'common');
        
        if (shouldShowColumn) {
          columns.push({
            key,
            label: key.replace(/^[fc]_/, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            type: getFieldType(key),
            sortable: true,
            featureType
          });
        }
      }
    });

    // Add custom label columns (only if showCustomLabels is true)
    if (showCustomLabels) {
      customLabelDefinitions.forEach(def => {
        columns.push({
          key: `custom_${def.label_key}`,
          label: def.label_key,
          type: def.label_type,
          sortable: true,
          featureType: 'custom',
          isCustomLabel: true,
          labelDefinition: def
        });
      });
    }

    return columns;
  };

  const formatValue = (value, type) => {
    if (value === null || value === undefined || value === '') return '';
    
    switch (type) {
      case 'currency':
        return `$${parseFloat(value).toFixed(2)}`;
      case 'number':
        return parseFloat(value).toFixed(2);
      case 'binary':
        // Handle both standard binary (1/0) and custom label binary (yes/no)
        if (value === 'yes' || value === 'Yes') return 'Yes';
        if (value === 'no' || value === 'No') return 'No';
        return value === '1' || value === 1 ? 'Yes' : value === '0' || value === 0 ? 'No' : '';
      default:
        // For text/categorical, return the value as-is (don't convert to string if it's already a string)
        return String(value);
    }
  };

  const columns = getVisibleColumns();

  // Format markdown text to HTML for rich display with video previews
  const formatMarkdown = (text, videoData = []) => {
    if (!text) return '';
    
    // Check if this text has already been processed
    if (text.includes('<!-- PROCESSED_BY_FORMATMARKDOWN -->')) {
      console.log('Text already processed, returning as-is...');
      return text;
    }
    
    console.log('Processing markdown text for the first time...');
    const sections = [
      'Top Winning Visual Hooks',
      'Top Winning Text Overlay Hooks', 
      'Top Winning Audio Hooks'
    ];
    
    const sectionVideoUrls = {};
    sections.forEach(section => {
      const sectionRegex = new RegExp(`\\*\\*${section.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\*\\*([\\s\\S]*?)(?=\\*\\*Top Winning|$)`, 'g');
      const sectionMatch = text.match(sectionRegex);
      if (sectionMatch) {
        const sectionContent = sectionMatch[0];
        const urls = extractVideoUrlsFromSection(sectionContent, videoData);
        sectionVideoUrls[section] = urls;
        console.log(`Section: ${section}, Found ${urls.length} video URLs:`, urls);
      }
    });
    
    let formattedText = text
      // Remove VIDEO_URL text from display (but keep Ad Name: for parsing)
      .replace(/\s*VIDEO_URL:\s*[^\s<>\n]+/g, '')
      // Convert **Top Winning** headers to styled headers FIRST (before general bold conversion)
      .replace(/^\*\*(Top Winning .*?)\*\*/gm, '<h2 class="text-xl font-bold text-gray-900 mt-6 mb-3 border-b border-gray-200 pb-2">$1</h2>')
      // Convert **bold** to <strong>
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Convert *italic* to <em>
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      // Convert ## headers to <h2>
      .replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold text-gray-900 mt-6 mb-3">$1</h2>')
      // Convert ### headers to <h3>
      .replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold text-gray-800 mt-4 mb-2">$1</h3>')
      // Convert **Pattern X:** to styled headers
      .replace(/^\*\*Pattern (\d+): (.*?)\*\*/gm, '<h4 class="text-md font-semibold text-blue-800 mt-4 mb-2">Pattern $1: $2</h4>')
      // Convert **Setting:**, **Action:**, etc. to styled labels
      .replace(/^\*\*(Setting|Action|Camera Angle|Lighting|Primary Focus):\*\*/gm, '<span class="font-semibold text-gray-800">$1:</span>')
      // Convert **Pattern Name:** to styled labels
      .replace(/^\*\*([^:]+):\*\*/gm, '<span class="font-semibold text-gray-800">$1:</span>')
      // Convert Example: to styled labels
      .replace(/^Example:/gm, '<span class="font-semibold text-green-700">Example:</span>')
      // Convert Ad Name: to styled labels
      .replace(/^Ad Name:/gm, '<span class="font-semibold text-blue-700">Ad Name:</span>')
      // Convert line breaks to <br>
      .replace(/\n/g, '<br/>')
      // Convert double line breaks to paragraph breaks
      .replace(/<br\/><br\/>/g, '</p><p class="mb-3">')
      // Wrap in paragraph tags
      .replace(/^(.*)$/gm, '<p class="mb-3">$1</p>')
      // Clean up empty paragraphs
      .replace(/<p class="mb-3"><\/p>/g, '')
      // Clean up paragraph tags around headers
      .replace(/<p class="mb-3">(<h[2-4][^>]*>.*<\/h[2-4]>)<\/p>/g, '$1')
      // Clean up paragraph tags around spans
      .replace(/<p class="mb-3">(<span[^>]*>.*<\/span>)<\/p>/g, '$1');

    // Add video previews after each section
    formattedText = addVideoPreviewsWithUrls(formattedText, sectionVideoUrls);
    
    // Add a unique marker to indicate this content has been processed
    formattedText = formattedText + '<!-- PROCESSED_BY_FORMATMARKDOWN -->';
    
    console.log('Final formatted text length:', formattedText.length);
    return formattedText;
  };

  // Add video previews after each section using pre-extracted URLs
  const addVideoPreviewsWithUrls = (html, sectionVideoUrls) => {
    // Check if video previews have already been added
    if (html.includes('<!-- PROCESSED_BY_FORMATMARKDOWN -->')) {
      console.log('Video previews already added, skipping...');
      return html;
    }

    console.log('Adding video previews to HTML...');

    const sections = [
      'Top Winning Visual Hooks',
      'Top Winning Text Overlay Hooks', 
      'Top Winning Audio Hooks'
    ];

    console.log('addVideoPreviewsWithUrls called with:', sectionVideoUrls);
    console.log('HTML length:', html.length);
    console.log('First 500 chars of HTML:', html.substring(0, 500));

    let processedHtml = html;
    
    sections.forEach(section => {
      const sectionRegex = new RegExp(`(<h2[^>]*>${section.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}<\/h2>)`, 'g');
      const matches = processedHtml.match(sectionRegex);
      console.log(`Looking for section "${section}", found ${matches ? matches.length : 0} matches:`, matches);
      
      if (matches && matches.length > 0) {
        // Process each match individually
        matches.forEach(match => {
          console.log(`Processing section: ${section}, match found:`, match);
          
          // Find the position of this specific match
          const matchIndex = processedHtml.indexOf(match);
          if (matchIndex === -1) return; // Match not found, skip
          
          // Find the next section or end of content
          const nextSectionIndex = matchIndex + match.length;
          const nextSectionMatch = processedHtml.substring(nextSectionIndex).match(/<h2[^>]*>Top Winning .*?<\/h2>/);
          const endIndex = nextSectionMatch ? nextSectionIndex + nextSectionMatch.index : processedHtml.length;
          
          const sectionContent = processedHtml.substring(nextSectionIndex, endIndex);
          const videoUrls = sectionVideoUrls[section] || [];
          
          console.log(`Section ${section} has ${videoUrls.length} video URLs:`, videoUrls);
          
          if (videoUrls.length > 0) {
            const videoPreviews = generateVideoPreviews(videoUrls, section);
            console.log(`Generated video previews for ${section}:`, videoPreviews.substring(0, 200) + '...');
            
            // Replace the entire section with header + content + video previews
            const newSectionContent = match + sectionContent + videoPreviews;
            processedHtml = processedHtml.substring(0, matchIndex) + newSectionContent + processedHtml.substring(endIndex);
          }
        });
      }
    });

    return processedHtml;
  };

  // Add video previews after each section
  const addVideoPreviews = (html, videoData) => {
    // Find sections and add video previews
    const sections = [
      'Top Winning Visual Hooks',
      'Top Winning Text Overlay Hooks', 
      'Top Winning Audio Hooks'
    ];

    sections.forEach(section => {
      const sectionRegex = new RegExp(`(<h2[^>]*>${section.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}<\/h2>)`, 'g');
      html = html.replace(sectionRegex, (match) => {
        // Find the next section or end of content
        const nextSectionIndex = html.indexOf(match) + match.length;
        const nextSectionMatch = html.substring(nextSectionIndex).match(/<h2[^>]*>Top Winning .*?<\/h2>/);
        const endIndex = nextSectionMatch ? nextSectionIndex + nextSectionMatch.index : html.length;
        
        const sectionContent = html.substring(nextSectionIndex, endIndex);
        
        // Extract video URLs from this section (before HTML conversion)
        const originalText = sectionContent.replace(/<[^>]*>/g, ''); // Remove HTML tags to get original text
        const videoUrls = extractVideoUrlsFromSection(originalText, videoData);
        
        if (videoUrls.length > 0) {
          const videoPreviews = generateVideoPreviews(videoUrls, section);
          return match + sectionContent + videoPreviews;
        }
        
        return match + sectionContent;
      });
    });

    return html;
  };

  // Extract video URLs from a section
  const extractVideoUrlsFromSection = (sectionContent, videoData) => {
    const urls = [];
    console.log('Extracting video URLs from section:', sectionContent.substring(0, 200) + '...');
    console.log('Video data available:', videoData.length, 'ads');
    
    // First, try to find VIDEO_URL patterns
    const videoUrlMatches = sectionContent.match(/VIDEO_URL:\s*([^\s<>\n]+)/g);
    console.log('VIDEO_URL matches found:', videoUrlMatches);
    if (videoUrlMatches) {
      videoUrlMatches.forEach(match => {
        const url = match.replace(/VIDEO_URL:\s*/g, '').trim();
        if (url && url !== '' && url.startsWith('http')) {
          // Find the corresponding ad data
          const adData = videoData.find(ad => ad.video_url === url);
          if (adData) {
            urls.push({
              url: url,
              adName: adData.ad_name,
              thumbstop: adData.thumbstop,
              holdRate: adData.hold_rate
            });
          }
        }
      });
    }
    
    // If no VIDEO_URL patterns found, try to match ad names from the content
    if (urls.length === 0) {
      console.log('No VIDEO_URL patterns found, trying ad name matching...');
      // Look for ad names in the content (they usually appear after "Ad Name:" or in examples)
      const adNameMatches = sectionContent.match(/Ad Name:\s*([^\n\r]+)/g);
      console.log('Ad Name matches found:', adNameMatches);
      if (adNameMatches) {
        adNameMatches.forEach(match => {
          const adName = match.replace(/Ad Name:\s*/g, '').trim();
          console.log('Looking for ad name:', adName);
          if (adName) {
            // Find the corresponding ad data by ad name
            const adData = videoData.find(ad => ad.ad_name === adName);
            console.log('Found ad data:', adData ? 'Yes' : 'No');
            if (adData && adData.video_url) {
              urls.push({
                url: adData.video_url,
                adName: adData.ad_name,
                thumbstop: adData.thumbstop,
                holdRate: adData.hold_rate
              });
            }
          }
        });
      }
      
      // Also try to match ad names that appear in examples (they might be quoted or in parentheses)
      const exampleMatches = sectionContent.match(/Example:.*?"([^"]+)"/g);
      if (exampleMatches) {
        exampleMatches.forEach(match => {
          // Extract quoted ad names from the example text
          const quotedAdName = match.match(/"([^"]+)"/);
          if (quotedAdName && quotedAdName[1]) {
            const adName = quotedAdName[1];
            const adData = videoData.find(ad => ad.ad_name === adName);
            if (adData && adData.video_url && !urls.find(u => u.url === adData.video_url)) {
              urls.push({
                url: adData.video_url,
                adName: adData.ad_name,
                thumbstop: adData.thumbstop,
                holdRate: adData.hold_rate
              });
            }
          }
        });
      }
      
      // Try to match ad names without quotes (they might be in the example text)
      const unquotedMatches = sectionContent.match(/Example:.*?([A-Za-z0-9_\-:]{20,})/g);
      if (unquotedMatches) {
        unquotedMatches.forEach(match => {
          // Extract potential ad names from the example text
          const potentialAdNames = match.match(/[A-Za-z0-9_\-:]{20,}/g);
          if (potentialAdNames) {
            potentialAdNames.forEach(potentialName => {
              const adData = videoData.find(ad => ad.ad_name === potentialName);
              if (adData && adData.video_url && !urls.find(u => u.url === adData.video_url)) {
                urls.push({
                  url: adData.video_url,
                  adName: adData.ad_name,
                  thumbstop: adData.thumbstop,
                  holdRate: adData.hold_rate
                });
              }
            });
          }
        });
      }
    }
    
    return urls;
  };

  // Generate video preview HTML
  const generateVideoPreviews = (videoUrls, section) => {
    if (videoUrls.length === 0) return '';
    
    console.log(`generateVideoPreviews called for ${section} with ${videoUrls.length} videos:`, videoUrls);
    
    const sectionTitle = section.replace('Top Winning ', '').replace(' Hooks', '');
    
    const html = `
      <div class="mt-6 p-4 bg-gray-50 rounded-lg">
        <h5 class="text-sm font-semibold text-gray-700 mb-3">📹 ${sectionTitle} Video Examples</h5>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          ${videoUrls.map((video, index) => `
            <div class="bg-white rounded-lg p-3 border hover:shadow-md transition-shadow cursor-pointer video-preview-item" 
                 data-video-url="${video.url}" 
                 data-ad-name="${video.adName.replace(/"/g, '&quot;')}"
                 data-index="${index}"
                 onclick="window.openVideoPreview && window.openVideoPreview('${video.url}', '${video.adName.replace(/'/g, "\\'")}')">
              <video class="w-full rounded mb-2" preload="metadata" controls>
                <source src="${video.url}" type="video/mp4">
              </video>
              <div class="text-xs text-gray-600 truncate" title="${video.adName}">${video.adName}</div>
              <div class="text-xs text-blue-600 mt-1">
                Hook: ${video.thumbstop}% | Hold: ${video.holdRate}%
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
    
    console.log(`Generated HTML for ${section}:`, html.substring(0, 300) + '...');
    return html;
  };

  // Open video preview modal
  const openVideoPreview = (videoUrl, adName) => {
    setPreviewModal({
      isOpen: true,
      assetUrl: videoUrl,
      mediaFormat: 'Video',
      adName: adName
    });
  };

  // Make openVideoPreview available globally for onclick handlers
  useEffect(() => {
    window.openVideoPreview = openVideoPreview;
    return () => {
      delete window.openVideoPreview;
    };
  }, []);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
          <p className="text-gray-600">Manage and edit ad creative labels and performance</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportToCSV}
            disabled={isExporting || ads.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Exporting...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Export CSV
              </>
            )}
          </button>
        </div>
      </div>

      {/* Filters and Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <label className="block text-sm font-medium text-gray-700">Media Type</label>
            <select
              value={mediaType}
              onChange={(e) => setMediaType(e.target.value)}
              className="border rounded px-3 py-2"
            >
              <option value="all">All</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
            
            {/* Feature Column Toggles */}
            <div className="flex items-center gap-2 ml-6">
              <span className="text-sm font-medium text-gray-700">Show Features:</span>
              <button
                onClick={() => setShowImageFeatures(!showImageFeatures)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  showImageFeatures 
                    ? 'bg-blue-100 text-blue-800 border border-blue-200' 
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                }`}
              >
                🔵 Image ({showImageFeatures ? 'Show' : 'Hide'})
              </button>
              <button
                onClick={() => setShowVideoFeatures(!showVideoFeatures)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  showVideoFeatures 
                    ? 'bg-green-100 text-green-800 border border-green-200' 
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                }`}
              >
                🟢 Video ({showVideoFeatures ? 'Show' : 'Hide'})
              </button>
              <button
                onClick={() => setShowCustomLabels(!showCustomLabels)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  showCustomLabels 
                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' 
                    : 'bg-gray-100 text-gray-600 border border-gray-200'
                }`}
              >
                🟡 Custom ({showCustomLabels ? 'Show' : 'Hide'})
              </button>
            </div>
          </div>
        </div>
        
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading ads...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map(column => (
                      <th
                        key={column.key}
                        className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                          column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                        } ${getFeatureBackgroundColor(column.featureType)}`}
                        onClick={column.sortable ? () => handleColumnSort(column.key) : undefined}
                      >
                        <div className="flex items-center justify-between">
                          <span>{column.label}</span>
                          {column.sortable && (
                            <span className="ml-1">
                              {getSortIcon(column.key)}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {ads.map(ad => (
                    <tr key={ad.raw_ad_id} className="hover:bg-gray-50">
                      {columns.map(column => {
                        // Get value for custom labels
                        let cellValue = ad[column.key];
                        if (column.isCustomLabel) {
                          const assetId = ad.asset_id;
                          const labelKey = column.labelDefinition.label_key;
                          cellValue = customLabelValues[assetId]?.[labelKey] || null;
                          // Debug: log if value is missing
                          if (cellValue === null && customLabelValues[assetId]) {
                            console.log(`Missing label value for asset ${assetId}, label ${labelKey}. Available labels:`, Object.keys(customLabelValues[assetId]));
                          }
                        }
                        
                        return (
                          <td key={column.key} className={`px-4 py-3 text-sm text-gray-900 ${getFeatureBackgroundColor(column.featureType)}`}>
                            {column.key === 'media_preview' ? (
                              renderMediaPreview(ad)
                            ) : column.key === 'ad_name' || column.key === 'format' ? (
                              <div className="font-medium">
                                {formatValue(ad[column.key], column.type)}
                              </div>
                            ) : column.isCustomLabel ? (
                              renderEditableCell(ad, column.key, cellValue, column.labelDefinition)
                            ) : column.key.startsWith('f_') || column.key.startsWith('cf_') || column.key.startsWith('video_') ? (
                              renderEditableCell(ad, column.key, ad[column.key])
                            ) : (
                              <div className="font-medium">
                                {formatValue(ad[column.key], column.type)}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing page {currentPage} of {totalPages}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* Media Preview Modal */}
      {showMediaModal && selectedMedia && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMediaModal(false);
            }
          }}
        >
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedMedia.name}
              </h3>
              <button
                onClick={() => setShowMediaModal(false)}
                className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-colors"
                title="Close (ESC)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-4">
              {selectedMedia.type === 'Image' ? (
                <div className="flex justify-center">
                  <img
                    src={selectedMedia.url}
                    alt={selectedMedia.name}
                    className="max-w-full max-h-[70vh] object-contain rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  />
                  <div className="flex items-center justify-center w-full h-64 bg-gray-100 rounded-lg" style={{display: 'none'}}>
                    <div className="text-center text-gray-500">
                      <div className="text-4xl mb-2">📷</div>
                      <div>Image not available</div>
                    </div>
                  </div>
                </div>
              ) : selectedMedia.type === 'Video' ? (
                <div className="flex justify-center">
                  <video
                    src={selectedMedia.url}
                    controls
                    className="max-w-full max-h-[70vh] rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      if (e.target.nextSibling) {
                        e.target.nextSibling.style.display = 'flex';
                      }
                    }}
                  >
                    Your browser does not support the video tag.
                  </video>
                  <div className="flex items-center justify-center w-full h-64 bg-gray-100 rounded-lg" style={{display: 'none'}}>
                    <div className="text-center text-gray-500">
                      <div className="text-4xl mb-2">🎥</div>
                      <div>Video not available</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 bg-gray-100 rounded-lg">
                  <div className="text-center text-gray-500">
                    <div className="text-4xl mb-2">❓</div>
                    <div>Media type not supported</div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-4 py-3 bg-gray-50 border-t">
              <div className="text-sm text-gray-600">
                <strong>Ad ID:</strong> {selectedMedia.adId}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Text Content Modal */}
      {showTextModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{textModalContent.title}</h3>
              <button
                onClick={() => setShowTextModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {textModalContent.content}
              </div>
            </div>
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowTextModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Analysis Results Modal */}
      {analysisResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Feature Analysis Results</h2>
              <button
                onClick={() => setAnalysisResults(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800">Total Ads Analyzed</h3>
                  <p className="text-2xl font-bold text-blue-900">{analysisResults.analysis.totalAds}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-green-800">Average {(filters?.correlationMetric || 'revenue').toUpperCase()}</h3>
                  <p className="text-2xl font-bold text-green-900">
                    {(filters?.correlationMetric || 'revenue') === 'ctr' ? `${analysisResults.analysis.avgMetric.toFixed(2)}%` : 
                     (filters?.correlationMetric || 'revenue') === 'roas' ? analysisResults.analysis.avgMetric.toFixed(2) :
                     (filters?.correlationMetric || 'revenue') === 'cpa' ? `$${analysisResults.analysis.avgMetric.toFixed(2)}` :
                     `$${analysisResults.analysis.avgMetric.toFixed(2)}`}
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-800">Analysis Type</h3>
                  <p className="text-lg font-bold text-purple-900 capitalize">{analysisResults.metadata.analysisType}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-orange-800">Total Ads with Performance</h3>
                  <p className="text-2xl font-bold text-orange-900">{analysisResults.metadata.totalAdsWithPerformance}</p>
                  <p className="text-xs text-orange-600">Before filtering</p>
                </div>
              </div>

              {/* Filter Info */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="text-sm font-medium text-gray-800 mb-3">Analysis Filters Applied</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                  <div>
                    <span className="font-medium">Date Range:</span> {analysisResults.metadata.dateRange} days
                  </div>
                  <div>
                    <span className="font-medium">Min Spend:</span> ${analysisResults.metadata.minSpend}
                  </div>
                  <div>
                    <span className="font-medium">Min Impressions:</span> {analysisResults.metadata.minImpressions}
                  </div>
                  <div>
                    <span className="font-medium">Media Type:</span> {analysisResults.metadata.analysisType}
                  </div>
                </div>
              </div>

              {/* Top Single Features */}
              {analysisResults.analysis.singleFeatures && analysisResults.analysis.singleFeatures.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🚀 Top Performing Single Features</h3>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Feature</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ads</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg {(filters?.correlationMetric || 'revenue').toUpperCase()}</th>
                          {((filters?.correlationMetric || 'revenue') === 'revenue' || (filters?.correlationMetric || 'revenue') === 'spend') && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total {(filters?.correlationMetric || 'revenue').toUpperCase()}</th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Example</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {analysisResults.analysis.singleFeatures.slice(0, 10).map((feature, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {feature.feature.replace(/^video_|^f_/, '').replace(/_/g, ' ')}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{feature.value}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{feature.count}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              {(filters?.correlationMetric || 'revenue') === 'ctr' ? `${feature.avgMetric}%` : 
                               (filters?.correlationMetric || 'revenue') === 'roas' ? feature.avgMetric :
                               (filters?.correlationMetric || 'revenue') === 'cpa' ? `$${feature.avgMetric}` :
                               `$${feature.avgMetric}`}
                            </td>
                            {((filters?.correlationMetric || 'revenue') === 'revenue' || (filters?.correlationMetric || 'revenue') === 'spend' || (filters?.correlationMetric || 'revenue') === 'cpa') && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${feature.totalMetric}</td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {renderThumbnail(feature.exampleAd)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Feature Combinations */}
              {analysisResults.analysis.featurePairs && analysisResults.analysis.featurePairs.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🎯 Top Feature Combinations (Pairs)</h3>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Combination</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ads</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg {(filters?.correlationMetric || 'revenue').toUpperCase()}</th>
                          {((filters?.correlationMetric || 'revenue') === 'revenue' || (filters?.correlationMetric || 'revenue') === 'spend') && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total {(filters?.correlationMetric || 'revenue').toUpperCase()}</th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Example</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {analysisResults.analysis.featurePairs.slice(0, 10).map((pair, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pair.combination}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pair.count}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              {(filters?.correlationMetric || 'revenue') === 'ctr' ? `${pair.avgMetric}%` : 
                               (filters?.correlationMetric || 'revenue') === 'roas' ? pair.avgMetric :
                               (filters?.correlationMetric || 'revenue') === 'cpa' ? `$${pair.avgMetric}` :
                               `$${pair.avgMetric}`}
                            </td>
                            {((filters?.correlationMetric || 'revenue') === 'revenue' || (filters?.correlationMetric || 'revenue') === 'spend' || (filters?.correlationMetric || 'revenue') === 'cpa') && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${pair.totalMetric}</td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {renderThumbnail(pair.exampleAd)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Top Feature Triples */}
              {analysisResults.analysis.featureTriples && analysisResults.analysis.featureTriples.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">🏆 Top Feature Combinations (Triples)</h3>
                  <div className="bg-white border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Combination</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ads</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg {(filters?.correlationMetric || 'revenue').toUpperCase()}</th>
                          {((filters?.correlationMetric || 'revenue') === 'revenue' || (filters?.correlationMetric || 'revenue') === 'spend') && (
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total {(filters?.correlationMetric || 'revenue').toUpperCase()}</th>
                          )}
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Example</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {analysisResults.analysis.featureTriples.slice(0, 10).map((triple, index) => (
                          <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{triple.combination}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{triple.count}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600">
                              {(filters?.correlationMetric || 'revenue') === 'ctr' ? `${triple.avgMetric}%` : 
                               (filters?.correlationMetric || 'revenue') === 'roas' ? triple.avgMetric :
                               (filters?.correlationMetric || 'revenue') === 'cpa' ? `$${triple.avgMetric}` :
                               `$${triple.avgMetric}`}
                            </td>
                            {((filters?.correlationMetric || 'revenue') === 'revenue' || (filters?.correlationMetric || 'revenue') === 'spend' || (filters?.correlationMetric || 'revenue') === 'cpa') && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${triple.totalMetric}</td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {renderThumbnail(triple.exampleAd)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 truncate pr-4" title={previewModal.adName}>
                {previewModal.adName}
              </h3>
              <button
                onClick={() => setPreviewModal({ ...previewModal, isOpen: false })}
                className="text-gray-400 hover:text-gray-600 text-2xl flex-shrink-0"
                title="Close"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4">
              {previewModal.mediaFormat === 'Video' ? (
                <video 
                  className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg"
                  controls
                  autoPlay
                  preload="metadata"
                  onError={(e) => {
                    console.error('Video preview error:', e);
                  }}
                >
                  <source src={previewModal.assetUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img 
                  src={previewModal.assetUrl} 
                  alt={previewModal.adName}
                  className="w-full h-auto max-h-[70vh] rounded-lg shadow-lg object-contain"
                  onError={(e) => {
                    console.error('Image preview error:', e);
                  }}
                />
              )}
            </div>
            
            {/* Footer */}
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={() => setPreviewModal({ ...previewModal, isOpen: false })}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hook Analysis Results Modal */}
      {hookAnalysisResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">Hook Analysis Results</h2>
              <button
                onClick={() => setHookAnalysisResults(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-purple-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-purple-800">Video Ads Analyzed</h3>
                  <p className="text-2xl font-bold text-purple-900">{hookAnalysisResults.metadata.totalAds}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-blue-800">Min Spend Threshold</h3>
                  <p className="text-2xl font-bold text-blue-900">${hookAnalysisResults.metadata.minSpend}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-green-800">Analysis Date Range</h3>
                  <p className="text-lg font-bold text-green-900">{hookAnalysisResults.metadata.days} days</p>
                </div>
              </div>

              {/* Hook Analysis Content */}
              <div className="bg-white border rounded-lg p-6">
                <div className="prose max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-em:text-gray-600">
                  <div dangerouslySetInnerHTML={{ __html: formatMarkdown(hookAnalysisResults.analysis, hookAnalysisResults.videoData || []) }} />
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex justify-end p-4 border-t border-gray-200">
              <button
                onClick={() => setHookAnalysisResults(null)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaLibrary;
